import { mapRepresentativesToDepartmentContactLists } from './common-mapper';

import type {
  WitnessCompanyRegistrationForm,
  WitnessCompanyDetailForm,
} from '../../types/company.types';
import type { DepartmentResponse } from '../../types';

export interface CreateWitnessCompanyRequest {
  name: string;
  tel: string;
  fax: string;
  contactPerson?: string;
  contactTel?: string;
  departmentContactLists: Array<{
    contactPersonCode: number;
    departmentCode: number;
  }>;
  isCommonCement: boolean;
}

/**
 * Common interface for witness company form data fields used in mapping
 */
interface WitnessCompanyFormFields {
  officialName: string;
  tel: string;
  fax: string;
  contactPerson?: string;
  contactTel?: string;
  representatives: Array<{
    department: string;
    salesPerson: { id: string; firstName: string; lastName: string; code: string };
  }>;
  isCommonCement: boolean;
}

/**
 * Shared helper function to map witness company form data to API request format
 * Converts form field names to API field names and IDs to codes
 */
const mapWitnessCompanyFormToRequest = (
  formData: WitnessCompanyFormFields,
  departments: DepartmentResponse[] = []
): CreateWitnessCompanyRequest => {
  const departmentContactLists = mapRepresentativesToDepartmentContactLists(
    formData.representatives,
    departments
  );

  return {
    name: formData.officialName,
    tel: formData.tel,
    fax: formData.fax,
    contactPerson: formData.contactPerson || undefined,
    contactTel: formData.contactTel || undefined,
    departmentContactLists,
    isCommonCement: formData.isCommonCement,
  };
};

/**
 * Maps witness company registration form data to API request format
 * Converts form field names to API field names and IDs to codes
 */
export const mapWitnessCompanyFormRequest = (
  formData: WitnessCompanyRegistrationForm,
  departments: DepartmentResponse[] = []
): CreateWitnessCompanyRequest => {
  return mapWitnessCompanyFormToRequest(formData, departments);
};

/**
 * Maps witness company detail form data to API request format for editing
 * Converts form field names to API field names and IDs to codes
 */
export const mappingEditWitnessCompanyData = (
  formData: WitnessCompanyDetailForm,
  departments: DepartmentResponse[] = []
): CreateWitnessCompanyRequest => {
  // Extract only the fields needed for mapping
  const formFields: WitnessCompanyFormFields = {
    officialName: formData.officialName,
    tel: formData.tel,
    fax: formData.fax,
    contactPerson: formData.contactPerson,
    contactTel: formData.contactTel,
    representatives: formData.representatives,
    isCommonCement: formData.isCommonCement,
  };
  return mapWitnessCompanyFormToRequest(formFields, departments);
};
